import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { BookOpen, FileDown, ShoppingBag, Mic, Leaf, User, ArrowRight, BadgeCheck, Clock, Sparkles, HeartHandshake } from "lucide-react";
import { SHOPIFY_URL, SHOPIFY_DOWNLOADS } from "../lib/config";
import { Seo } from "../components/Seo";

const HERO_PORTRAIT = "/brand/laura.webp";
const BOOK_COVER = "/brand/book-cover.webp";
const ALFRED = "/brand/alfred.webp";
const QUOTE_LIFESTYLE = "https://images.unsplash.com/photo-1458014854819-1a40aa70211c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxuYXR1cmUlMjBjb3Vuc2VsaW5nJTIwZ3Jvd3RofGVufDB8fHx8MTc4NDE2ODQ2Nnww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: BookOpen, title: "Books", text: "Thought-provoking books and workbooks for personal growth.", link: "/books", cta: "Explore Books" },
  { icon: FileDown, title: "Learning Resources", text: "Practical tools and educational resources to go deeper.", href: SHOPIFY_DOWNLOADS, cta: "Browse Learning Resources" },
  { icon: ShoppingBag, title: "Shop", text: "Books, journals, and resources to support your journey.", href: SHOPIFY_URL, cta: "Visit the Shop" },
  { icon: Mic, title: "Podcast", text: "Real conversations. Practical wisdom. Coming soon!", link: null, cta: "Coming Soon" },
  { icon: Leaf, title: "Resources", text: "Companion downloads, worksheets, and practical tools for everyday growth.", link: "/resources", cta: "Explore Resources" },
  { icon: User, title: "About Laura", text: "Learn more about Laura's story, mission, and approach.", link: "/about", cta: "Meet Laura" },
];

const CREDENTIALS = [
  { icon: BadgeCheck, label: "Licensed Clinical Social Worker (LCSW)" },
  { icon: Clock, label: "13+ Years of Clinical Experience" },
  { icon: Sparkles, label: "EMDR Trained Therapist" },
  { icon: BookOpen, label: "Author of Welcome to Adulthood" },
  { icon: Leaf, label: "Founder of Laura Combs Productions" },
  { icon: HeartHandshake, label: "Creating Practical Life Skills Resources for Individuals, Families, and Communities" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const welcomeBook = featured.find((p) => p.title === "Welcome to Adulthood");
  const bookLink = welcomeBook ? `/store/${welcomeBook.id}` : "/store?type=book";

  return (
    <div data-testid="home-page">
      <Seo
        title="Books, Education & Practical Tools for Stronger Lives"
        description="Laura Combs Productions helps people build stronger lives through practical education, compassionate guidance, and everyday wisdom. Explore books, resources, and Alfred's guide to adulthood."
        path="/"
      />
      {/* HERO */}
      <section className="grid lg:grid-cols-[0.9fr_1.1fr_0.9fr] items-stretch bg-ivory grain">
        <div className="min-h-[380px] lg:min-h-[600px] order-1">
          <img src={HERO_PORTRAIT} alt="Laura Combs, founder of Laura Combs Productions, smiling at her writing desk" className="w-full h-full object-cover object-top" fetchpriority="high" decoding="async" width="588" height="506" />
        </div>

        <div className="order-2 lg:order-2 flex flex-col justify-center px-8 lg:px-12 py-14 text-center fade-up">
          <span className="text-gold text-xs uppercase tracking-[0.35em]">Laura Combs Productions</span>
          <div className="flex items-center justify-center gap-3 my-4">
            <span className="h-px w-10 bg-gold/50" />
            <Leaf className="w-4 h-4 text-gold" />
            <span className="h-px w-10 bg-gold/50" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl xl:text-6xl text-forest leading-[1.08]">
            Books. Education.
            <br /> Practical Tools.
            <br /> <span className="text-gold">Stronger Lives.</span>
          </h1>
          <p className="text-charcoal/70 mt-6 leading-relaxed max-w-sm mx-auto">
            Helping people build stronger lives through practical education, compassionate
            guidance, and everyday wisdom.
          </p>
          <div className="mt-8">
            <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-full bg-forest hover:bg-forest-light text-ivory px-8 h-12 gap-2 transition-colors" data-testid="hero-explore-btn">
                Explore the Shop <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>

        <div className="order-3 lg:order-3 relative flex items-center justify-center px-8 py-14 lg:py-0">
          <div className="relative">
            <img src={BOOK_COVER} alt="Welcome to Adulthood" className="w-52 xl:w-64 rounded-lg shadow-2xl rotate-1" fetchpriority="high" decoding="async" />
          </div>
        </div>
      </section>

      {/* FEATURE ROW */}
      <section className="bg-ivory border-y border-gold/25">
        <div className="max-w-[1400px] mx-auto px-6 py-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-12">
          {FEATURES.map((f) => (
            <div key={f.title} className="text-center flex flex-col items-center" data-testid={`feature-${f.title.toLowerCase().replace(/ /g, "-")}`}>
              <f.icon className="w-8 h-8 text-forest mb-4" strokeWidth={1.3} />
              <h3 className="font-serif text-xl text-forest">{f.title}</h3>
              <p className="text-charcoal/60 text-sm mt-2 leading-relaxed flex-1">{f.text}</p>
              {f.href ? (
                <a href={f.href} target="_blank" rel="noopener noreferrer" className="text-gold text-xs uppercase tracking-wider mt-4 hover:text-forest transition-colors inline-flex items-center gap-1">
                  {f.cta} <ArrowRight className="w-3 h-3" />
                </a>
              ) : f.link ? (
                <Link to={f.link} className="text-gold text-xs uppercase tracking-wider mt-4 hover:text-forest transition-colors inline-flex items-center gap-1">
                  {f.cta} <ArrowRight className="w-3 h-3" />
                </Link>
              ) : (
                <span className="text-gold/60 text-xs uppercase tracking-wider mt-4">{f.cta}</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED BOOK */}
      <section className="max-w-[1400px] mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-[5/4]">
            <img src={QUOTE_LIFESTYLE} alt="Open journal and pen in a calm, sunlit setting" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
            <div className="absolute top-8 left-8 bg-forest text-ivory rounded-2xl p-6 max-w-[220px] shadow-xl">
              <p className="font-serif text-2xl leading-snug">"You don't have to figure it out alone."</p>
              <Leaf className="w-5 h-5 text-gold mt-3 mx-auto" />
            </div>
            <img src={BOOK_COVER} alt="Welcome to Adulthood" className="absolute -bottom-6 right-6 w-32 rounded-lg shadow-2xl hidden sm:block" loading="lazy" decoding="async" />
          </div>

          <div>
            <span className="text-gold text-xs uppercase tracking-[0.35em]">Featured Book</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest mt-3">Welcome to Adulthood</h2>
            <p className="text-charcoal/70 italic mt-3 text-lg">A Practical Guide to Building the Life You Want</p>
            {welcomeBook?.coming_soon && (
              <span className="inline-flex items-center gap-2 mt-4 bg-gold/15 border border-gold/40 rounded-full px-4 py-1.5 text-sm text-forest font-medium" data-testid="featured-coming-soon">
                <span className="w-2 h-2 rounded-full bg-gold" />
                {welcomeBook.release_note || "Coming Soon"}
              </span>
            )}
            <p className="text-charcoal/70 mt-6 leading-relaxed">
              A step-by-step guide for young adults navigating real life. Money. Housing.
              Education. Career. Protection. Goals. With warm, practical guidance by your side,
              you've got this.
            </p>
            <Link to={bookLink} className="inline-block mt-8">
              <Button className="rounded-full bg-forest hover:bg-forest-light text-ivory px-8 h-12 gap-2 transition-colors" data-testid="featured-book-btn">
                Learn More About the Book <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* MEET LAURA */}
      <section className="bg-ivory">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/5] order-1 lg:order-1">
            <img src={HERO_PORTRAIT} alt="Portrait of Laura Combs, LCSW" className="w-full h-full object-cover object-top" loading="lazy" decoding="async" />
          </div>
          <div className="order-2">
            <span className="text-gold text-xs uppercase tracking-[0.35em]">The Founder</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest mt-3 leading-tight">Meet Laura</h2>
            <p className="text-charcoal/70 mt-6 leading-relaxed">
              Hi, I'm Laura Combs. For more than a decade I've helped people navigate trauma,
              anxiety, relationships, and life's biggest transitions. Along the way I realized
              many of the practical skills people need to build healthy, successful lives are
              never taught in school. Laura Combs Productions was created to bridge that gap
              through books, practical tools, educational resources, and compassionate guidance
              that people can use every day.
            </p>
            <Link to="/about" className="inline-block mt-8">
              <Button className="rounded-full bg-forest hover:bg-forest-light text-ivory px-8 h-12 gap-2 transition-colors" data-testid="meet-laura-btn">
                Learn More About Laura <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="bg-white border-y border-gold/25">
        <div className="max-w-[1100px] mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-serif text-4xl sm:text-5xl text-forest leading-tight">
              Trusted Guidance. Practical Experience.
            </h2>
            <p className="text-charcoal/70 mt-5 leading-relaxed">
              Helping people build stronger lives through education, compassion, and
              real-world experience.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-x-12 gap-y-8 mt-14">
            {CREDENTIALS.map((c) => (
              <div key={c.label} className="flex items-start gap-4" data-testid={`credential-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                <span className="shrink-0 w-12 h-12 rounded-full bg-ivory border border-gold/40 flex items-center justify-center">
                  <c.icon className="w-5 h-5 text-gold" strokeWidth={1.6} />
                </span>
                <p className="text-charcoal/80 leading-relaxed pt-2.5">{c.label}</p>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-16 pt-10 border-t border-gold/20 text-center">
            <p className="font-serif text-2xl sm:text-[1.7rem] text-forest leading-relaxed italic">
              "Everything I create is built on one belief: practical knowledge can change
              lives. Whether you're reading a book, exploring a resource, or following Alfred's
              guidance, my goal is to provide trustworthy, compassionate education that helps
              people move forward with confidence."
            </p>
            <p className="text-gold text-sm uppercase tracking-[0.2em] mt-8" data-testid="credentials-serving-line">
              Serving readers, families, educators, and helping professionals.
            </p>
          </div>
        </div>
      </section>

      {/* MEET ALFRED */}
      <section className="bg-white border-y border-gold/25">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <span className="text-gold text-xs uppercase tracking-[0.35em]">Your Guide</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest mt-3 leading-tight">Meet Alfred</h2>
            <p className="text-charcoal/70 mt-6 leading-relaxed">
              Alfred is your guide through adulthood. With wisdom, encouragement, and practical
              advice, Alfred helps young adults navigate money, housing, careers, education,
              relationships, insurance, and life's everyday decisions. Think of Alfred as the
              mentor everyone deserves.
            </p>
            <Link to={bookLink} className="inline-block mt-8">
              <Button className="rounded-full bg-forest hover:bg-forest-light text-ivory px-8 h-12 gap-2 transition-colors" data-testid="meet-alfred-btn">
                Meet Alfred <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="order-1 lg:order-2 flex justify-center">
            <img src={ALFRED} alt="Alfred the Owl" className="w-auto max-h-[480px] object-contain drop-shadow-2xl" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>

      {/* SHOP CTA */}
      <section className="bg-white border-y border-gold/25">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-gold text-xs uppercase tracking-[0.35em]">The Shop</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest mt-3 leading-tight">
              Books &amp; Educational Resources
            </h2>
            <p className="text-charcoal/70 mt-6 leading-relaxed max-w-lg">
              Browse the full collection of books, guided journals, and practical resources.
              Every order is handled securely through our shop with fast, friendly service.
            </p>
            <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className="inline-block mt-8">
              <Button className="rounded-full bg-forest hover:bg-forest-light text-ivory px-8 h-12 gap-2 transition-colors" data-testid="visit-shop-btn">
                Visit the Shop <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-2xl overflow-hidden aspect-[3/4] shadow-md bg-ivory flex items-center justify-center">
              <img src={BOOK_COVER} alt="Featured book" className="w-full h-full object-contain p-2" loading="lazy" decoding="async" />
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[3/4] shadow-md col-span-2">
              <img src={QUOTE_LIFESTYLE} alt="Journaling and growth" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="max-w-[1400px] mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 rounded-2xl overflow-hidden shadow-lg">
          <div className="bg-forest text-ivory p-12 lg:p-16 flex flex-col justify-center">
            <span className="text-gold text-xs uppercase tracking-[0.35em] mb-4">Our Mission</span>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-6">
              Growing Stronger Lives
            </h2>
            <p className="text-ivory/80 leading-relaxed">
              Growth rarely happens overnight. Like every seed, each person has the capacity to
              grow with the right encouragement, practical knowledge, and support. Laura Combs
              Productions exists to provide tools that help people build meaningful, confident
              lives—one step at a time.
            </p>
            <Link to="/about" className="mt-8">
              <Button className="rounded-full bg-gold hover:bg-gold/90 text-forest px-8 h-12 transition-colors" data-testid="mission-learn-btn">
                Learn Our Story
              </Button>
            </Link>
          </div>
          <div className="min-h-[320px]">
            <img src={QUOTE_LIFESTYLE} alt="New growth" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>
    </div>
  );
}
